import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { vendorInvoiceAPI } from "../../helpers/api/vendorInvoice";

export const fetchCostTypesAndCategories = createAsyncThunk(
  "vendorInvoiceConfig/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const [costTypes, categories, subCategories] = await Promise.all([
        vendorInvoiceAPI.getAllCostTypes(),
        vendorInvoiceAPI.getAllCategories(),
        vendorInvoiceAPI.getAllSubCategories(),
      ]);
      return { costTypes, categories, subCategories };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load data");
    }
  }
);

export const createCostType = createAsyncThunk(
  "vendorInvoiceConfig/createCostType",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.createCostType(data);
      toast.success("Cost type created");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to create cost type");
      return rejectWithValue(error.message);
    }
  }
);

export const updateCostType = createAsyncThunk(
  "vendorInvoiceConfig/updateCostType",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.updateCostType(id, data);
      toast.success("Cost type renamed");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to rename cost type");
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCostType = createAsyncThunk(
  "vendorInvoiceConfig/deleteCostType",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.deleteCostType(id);
      toast.success("Cost type deleted");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to delete cost type");
      return rejectWithValue(error.message);
    }
  }
);

export const createCategory = createAsyncThunk(
  "vendorInvoiceConfig/createCategory",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.createCategory(data);
      toast.success("Category created");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to create category");
      return rejectWithValue(error.message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  "vendorInvoiceConfig/updateCategory",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.updateCategory(id, data);
      toast.success("Category updated");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to update category");
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "vendorInvoiceConfig/deleteCategory",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.deleteCategory(id);
      toast.success("Category deleted");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to delete category");
      return rejectWithValue(error.message);
    }
  }
);

export const createSubCategory = createAsyncThunk(
  "vendorInvoiceConfig/createSubCategory",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.createSubCategory(data);
      toast.success("Sub-category created");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to create sub-category");
      return rejectWithValue(error.message);
    }
  }
);

export const updateSubCategory = createAsyncThunk(
  "vendorInvoiceConfig/updateSubCategory",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.updateSubCategory(id, data);
      toast.success("Sub-category updated");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to update sub-category");
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSubCategory = createAsyncThunk(
  "vendorInvoiceConfig/deleteSubCategory",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.deleteSubCategory(id);
      toast.success("Sub-category deleted");
      dispatch(fetchCostTypesAndCategories());
    } catch (error) {
      toast.error("Failed to delete sub-category");
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBudgetsByPhase = createAsyncThunk(
  "vendorInvoiceConfig/fetchBudgetsByPhase",
  async (phaseId, { rejectWithValue }) => {
    try {
      return await vendorInvoiceAPI.getBudgetsByPhase(phaseId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveBudget = createAsyncThunk(
  "vendorInvoiceConfig/saveBudget",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      if (id) {
        await vendorInvoiceAPI.updateBudget(id, data);
      } else {
        await vendorInvoiceAPI.createBudget(data);
      }
      toast.success("Budget saved");
      dispatch(fetchBudgetsByPhase(data.phase));
    } catch (error) {
      toast.error("Failed to save budget");
      return rejectWithValue(error.message);
    }
  }
);

export const removeBudget = createAsyncThunk(
  "vendorInvoiceConfig/removeBudget",
  async ({ id, phaseId }, { dispatch, rejectWithValue }) => {
    try {
      await vendorInvoiceAPI.deleteBudget(id);
      toast.success("Budget removed");
      dispatch(fetchBudgetsByPhase(phaseId));
    } catch (error) {
      toast.error("Failed to remove budget");
      return rejectWithValue(error.message);
    }
  }
);
